/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import {
  DeliveryType,
  type Path,
  type ServiceWithEstimate,
} from '@ahm/api-wrappers-core';
import { formatCurrency, generateLetterAvatar } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { useLocationStore } from '@ahm/react-map/store';
import {
  Accordion,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Image,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutationState } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useGetServiceTypes } from '../../api/service';
import BookingServiceInfoTooltip from './booking-service-info-tooltip';
import {
  getMaxCod,
  showAlertNoService,
  surgeIcon,
} from './service-shared-utils';
import classes from './services.module.css';
import { getTruckServiceErrorMessage } from './truck-service-utils';
import { ROUTES } from '@/config/router';
import { useDetectBanHour, useRemoteConfigHook } from '@/hooks';
import { ActionIndexPath } from '@/lib/constants';
import { getServiceType } from '@/lib/utils';
import { useLocaleSuffix, usePathname } from '@/navigation';
import { TruckGroup } from '@/stores/booking/booking-store';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type UseTruckServiceReturn = {
  isLoading: boolean;
  isDisabled: boolean;
  isHiddenTruck: boolean;
  estimatedServices: ServiceWithEstimate[];
  pickupPath: Path | null;
  onOpenPopupAllService: () => void;
  onClosePopupAllService: () => void;
  renderEstimatedServices: () => JSX.Element | JSX.Element[] | null;
  truckGroupedServices: any;
  isBulkyNewFlow: boolean;
};

export function useTruckService(): UseTruckServiceReturn {
  const isMobile = useMediaQuery(`(max-width: 767px)`);
  const localeSuffix = useLocaleSuffix();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const loadServiceParam = searchParams.get('load_service');
  const [hasServiceAvailable, setHasServiceAvailable] = useState(false);

  const { isTruckService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const { session, webInAppConfig } = useAuth();
  const user = session?.user;

  const { surgePriceConfig } = useRemoteConfigHook();
  const { checkServiceInBanFn } = useDetectBanHour();

  const {
    selectedService,
    setSelectedService,
    serviceTypes,
    setServiceTypes,
    setIsEstimateAllService,
    estimatedAllService,
    setOpenPopupAllService,
    truckSelectedGroup,
    setTruckSelectedGroup,
    isOpenPopupAllService,
    setAlertBooking,
    setIsSuggestPromotion,
  } = useBookingStore((state) => state);
  const { paths, loading: loadingLocation } = useLocationStore(
    (state) => state
  );
  const [activeItem, setActiveItem] = useState('');

  const estimatedServices = useMemo((): ServiceWithEstimate[] => {
    if (!serviceTypes?.length) return [];
    if (!estimatedAllService?.length) return serviceTypes;

    const estimateMap = new Map<string, any>();
    for (const estimate of estimatedAllService as any[]) {
      estimateMap.set(estimate.service_id, estimate);
    }

    return serviceTypes.map((service) => {
      const serviceWithEstimate: ServiceWithEstimate = { ...service };
      const matchedEstimate = estimateMap.get(service._id);

      if (matchedEstimate) {
        serviceWithEstimate.estimate_data = matchedEstimate as any;
      }

      return serviceWithEstimate;
    });
  }, [serviceTypes, estimatedAllService]);

  const truckGroupedServices = useMemo(() => {
    if (!isTruckService) return null;

    const groupedServices: any = {};
    const allowedTruckGroups = [
      TruckGroup.express,
      TruckGroup.sameday,
      TruckGroup.corporate,
      TruckGroup.standard,
    ];
    for (const groupName of allowedTruckGroups) {
      const servicesInGroup = estimatedServices.filter(
        (service) => service.group_name_en_us === groupName
      );

      if (servicesInGroup.length > 0) {
        const firstService: any = servicesInGroup[0];

        const minPrice = Math.min(
          ...servicesInGroup.map((service: any) => service.estimated_price || 0)
        );

        groupedServices[groupName] = {
          group_name_en_us: firstService.group_name_en_us,
          group_name_vi_vn: firstService.group_name_vi_vn,
          group_description: firstService.group_description || '',
          estimated_price: minPrice,
          services: servicesInGroup,
          icon: firstService?.group_icon_url || '',
        };
      }
    }
    if (
      !groupedServices?.[truckSelectedGroup] &&
      Object.keys(groupedServices).length > 0
    ) {
      setTruckSelectedGroup(Object.keys(groupedServices)[0]);
    }
    return groupedServices;
  }, [
    estimatedServices,
    isTruckService,
    truckSelectedGroup,
    setTruckSelectedGroup,
  ]);

  // Use shared surgeIcon utility

  const onChangeVehicle = useCallback(
    (serviceId) => {
      if (!serviceId) return;

      const service = truckGroupedServices?.[truckSelectedGroup]?.services.find(
        (s) => s._id === serviceId
      );

      if (service) {
        setSelectedService(service);
        close();
        setIsEstimateAllService(false);
      }
    },
    [
      truckGroupedServices,
      truckSelectedGroup,
      setSelectedService,
      setIsEstimateAllService,
    ]
  );

  const handleSelectVehicle = useCallback(
    (value) => {
      if (!value) return;

      if (isMobile) {
        setActiveItem(value);
      } else {
        setIsSuggestPromotion(true);
        onChangeVehicle(value);
      }
    },
    [isMobile, onChangeVehicle, setIsSuggestPromotion]
  );

  const open = useCallback(
    () => setOpenPopupAllService(true),
    [setOpenPopupAllService]
  );
  const close = useCallback(() => {
    setOpenPopupAllService(false);
    setIsEstimateAllService(false);
  }, [setOpenPopupAllService, setIsEstimateAllService]);

  // Pending mutations tracking
  const isEstimatingIntercityOrder = useMutationState({
    filters: { status: 'pending', mutationKey: ['estimateIntercityOrderFn'] },
    select: (mutation) => mutation.state.status,
  });
  const isEstimatingNewOrder = useMutationState({
    filters: { status: 'pending', mutationKey: ['estimateNewOrderFn'] },
    select: (mutation) => mutation.state.status,
  });

  const { pickupPath, currentDeliveryType } = useMemo(() => {
    const pickupPath = paths[ActionIndexPath.Pickup] ?? null;

    let deliveryType;
    switch (pathname) {
      case ROUTES.nationwide:
        deliveryType = DeliveryType.Nationwide;
        break;
      case ROUTES.truck:
        deliveryType = DeliveryType.Truck;
        break;
      case ROUTES.rental:
        deliveryType = DeliveryType.Rental;
        break;
      default:
        deliveryType = undefined;
    }

    return { pickupPath, currentDeliveryType: deliveryType };
  }, [paths, pathname]);

  const { data: serviceTypesData, isLoading: isLoadingServiceTypes } =
    useGetServiceTypes(
      {
        lat: Number(pickupPath?.lat),
        lng: Number(pickupPath?.lng),
        delivery_type: currentDeliveryType,
        user_mobile: (loadServiceParam && user?._id) ?? undefined, // for Onwheel miniapp
      },
      {
        enabled: Boolean(user && pickupPath?.lat && pickupPath?.lng),
        staleTime: 10 * 1000, // 10s
      }
    );

  useEffect(() => {
    if (isLoadingServiceTypes) return;

    if (!serviceTypesData?.length) {
      showAlertNoService(setAlertBooking, paths?.length ?? 0);
      return;
    }

    const webInAppId = webInAppConfig?.id;
    const filteredServices = serviceTypesData.filter((s) => {
      const enablePlatform = s.enable_platform ?? [];
      return (
        !webInAppId ||
        enablePlatform.length === 0 ||
        enablePlatform.includes(webInAppId)
      );
    });

    const servicesByPlatform = filteredServices.map((s) => {
      const { max_cod, max_cod_per_stop_point } = getMaxCod(
        s,
        user,
        webInAppConfig
      );
      return {
        ...s,
        max_cod,
        max_cod_per_stop_point,
        payment_method:
          webInAppConfig?.paymentMethods?.map((method) => ({
            payment: method,
          })) ?? [],
      };
    });

    if (servicesByPlatform.length === 0) {
      showAlertNoService(setAlertBooking, paths?.length ?? 0);
      return;
    }

    // Set truck services directly
    setServiceTypes(servicesByPlatform, pathname);
  }, [
    serviceTypesData,
    isLoadingServiceTypes,
    webInAppConfig?.id,
    webInAppConfig?.paymentMethods,
    pathname,
  ]);

  const handleOpenAllService = useCallback(() => {
    open();
    if (paths.length < 2) return;

    setIsEstimateAllService(true);
  }, [open, paths.length, setIsEstimateAllService]);

  const handleSelectService = useCallback(() => {
    if (activeItem) {
      setIsSuggestPromotion(true);
      onChangeVehicle(activeItem);
    }
  }, [activeItem, onChangeVehicle]);

  useEffect(() => {
    if (selectedService?._id && isOpenPopupAllService) {
      setActiveItem(selectedService._id);
    }
  }, [selectedService?._id, isOpenPopupAllService]);

  const getServiceCategories = useCallback(
    (services) => {
      if (!services?.length) {
        return { recommendedServices: [], otherServices: [] };
      }

      const recommendedServices: any[] = [];
      const otherServices: any[] = [];
      let hasAvailable = false;

      // Single pass through services for categorization
      for (const service of services) {
        const isServiceRestrictedByBan = !!checkServiceInBanFn(service);

        const errorMessage = getTruckServiceErrorMessage(
          service,
          isServiceRestrictedByBan,
          service.estimate_data?.error
        )?.description;

        // If no error, service is available
        if (!errorMessage) {
          hasAvailable = true;
        }

        // Add to appropriate category with error message
        const serviceWithError = { ...service, errorMessage };
        if (errorMessage) {
          otherServices.push(serviceWithError);
        } else {
          recommendedServices.push(serviceWithError);
        }
      }

      // Only update state when necessary
      if (hasAvailable !== hasServiceAvailable) {
        setHasServiceAvailable(hasAvailable);
      }

      return { recommendedServices, otherServices };
    },
    [checkServiceInBanFn, hasServiceAvailable]
  );

  // Optimized renderEstimatedServices with better component structure
  const renderEstimatedServices = useCallback(() => {
    if (!truckGroupedServices?.[truckSelectedGroup]?.services?.length) {
      return null;
    }

    const allServices = truckGroupedServices[truckSelectedGroup].services;
    const { recommendedServices, otherServices } =
      getServiceCategories(allServices);

    const renderServiceItem = (service, isRecommended = true) => {
      const isServiceSelected = activeItem === service._id;
      const isDisabled = !isRecommended;

      return (
        <Accordion.Item
          component="div"
          style={(theme) => ({
            border: `1px solid ${
              isServiceSelected
                ? theme.colors.primary[4]
                : isRecommended
                  ? theme.colors.neutral[3]
                  : theme.colors.neutral[2]
            }`,
            borderRadius: 8,
          })}
          bg={
            isDisabled ? 'neutral.1' : isServiceSelected ? 'primary.0' : 'white'
          }
          key={service._id}
          value={service._id}
          pos="relative"
          mt={8}
        >
          <Accordion.Control
            style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
          >
            <Box
              className={classes.button}
              p={0}
              w="100%"
              h="auto"
              bg="transparent"
              style={{
                position: 'relative',
                border: 'none',
                display: 'flex',
                alignItems: 'stretch',
                width: '100%',
                padding: 0,
              }}
            >
              <Box
                mr={rem(8)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Avatar
                  opacity={isDisabled ? 0.5 : 1}
                  src={service.icon_url}
                  variant="transparent"
                  radius="sm"
                  size={rem(40)}
                  alt={service[`name_${localeSuffix}`]}
                  styles={{ image: { objectFit: 'contain' } }}
                >
                  {generateLetterAvatar(service[`name_${localeSuffix}`])}
                </Avatar>
              </Box>
              <Stack gap={rem(2)} w="100%" justify="flex-start">
                <Group
                  wrap="nowrap"
                  justify="space-between"
                  w="100%"
                  gap={rem(4)}
                >
                  <Group wrap="nowrap" w="100%" gap={rem(4)}>
                    <Title
                      ta="left"
                      lineClamp={1}
                      order={5}
                      fw={600}
                      c={isDisabled ? 'neutral.5' : 'neutral.7'}
                    >
                      {service[`name_${localeSuffix}`]}
                    </Title>
                    <span>
                      <BookingServiceInfoTooltip service={service} />
                    </span>
                    {service?.badge && (
                      <Badge
                        style={{ textTransform: 'capitalize' }}
                        bg={service.badge.background_color ?? 'red.6'}
                        c={service.badge.text_color ?? 'white'}
                        size="md"
                        radius="xl"
                      >
                        {service.badge[`description_${localeSuffix}`] || 'Mới'}
                      </Badge>
                    )}
                  </Group>
                  {isRecommended && (
                    <Box flex="none">
                      {service.estimate_data?.data ? (
                        <Group
                          wrap="nowrap"
                          gap={rem(4)}
                          align="end"
                          justify="center"
                        >
                          {service.estimate_data.data.subtotal_price >
                          service.estimate_data.data.total_price ? (
                            <Text size="xs" c="neutral.5" td="line-through">
                              {formatCurrency(
                                service.estimate_data.data.subtotal_price
                              )}
                            </Text>
                          ) : null}
                          <Title order={6} c="primary.5">
                            {formatCurrency(
                              service.estimate_data.data.total_price
                            )}
                          </Title>
                          {surgeIcon(service, surgePriceConfig)}
                        </Group>
                      ) : isEstimatingIntercityOrder?.length > 0 ||
                        isEstimatingNewOrder.length > 0 ? (
                        <Skeleton width="100%" height={rem(16)} />
                      ) : null}
                    </Box>
                  )}
                </Group>
                <Text
                  c={isDisabled ? 'neutral.5' : 'neutral.6'}
                  size="xs"
                  fw={500}
                  align="left"
                >
                  {service[`description_${localeSuffix}`]}
                </Text>
                {!isRecommended && service.errorMessage && (
                  <Text c="red.6" size="xs" fw={500} align="left">
                    {service.errorMessage}
                  </Text>
                )}
              </Stack>
              {isServiceSelected && (
                <Box
                  hiddenFrom="sm"
                  position="absolute"
                  top={0}
                  left={0}
                  bottom={0}
                  height="100%"
                  width={rem(4)}
                  bg="primary.6"
                />
              )}
            </Box>
          </Accordion.Control>
          {isRecommended && (
            <Accordion.Panel style={{ paddingBottom: 0 }}>
              <Image
                src={service?.vehicle_info?.images?.[0] || ''}
                alt="vehicle-image"
                fit="contain"
              />
            </Accordion.Panel>
          )}
        </Accordion.Item>
      );
    };

    const servicesContent = (
      <>
        {recommendedServices.length > 0 && (
          <Box py={12} px={16}>
            <Title order={5} mb={4}>
              Gợi ý cho bạn
            </Title>
            <Text size="xs" c="neutral.6" mb={12}>
              Các loại xe phù hợp với hàng hóa của bạn
            </Text>
            <Accordion
              value={activeItem}
              onChange={handleSelectVehicle}
              styles={{ chevron: { display: 'none' } }}
              variant="separated"
            >
              {recommendedServices.map((service) =>
                renderServiceItem(service, true)
              )}
            </Accordion>
          </Box>
        )}

        {otherServices.length > 0 && (
          <Box py={12} px={16} mt={recommendedServices.length > 0 ? 8 : 0}>
            <Title order={5} mb={recommendedServices.length > 0 ? 12 : 4}>
              Loại xe khác
            </Title>
            <Accordion
              value={activeItem}
              onChange={handleSelectVehicle}
              styles={{ chevron: { display: 'none' } }}
              variant="separated"
            >
              {otherServices.map((service) =>
                renderServiceItem(service, false)
              )}
            </Accordion>
          </Box>
        )}
      </>
    );

    // Return with confirmation button on mobile
    if (isMobile) {
      return (
        <>
          {servicesContent}
          <Card
            pos="sticky"
            p={16}
            bottom={0}
            bg="white"
            w="100%"
            styles={{
              root: {
                boxShadow: '0px -2px 4px 0px rgba(0, 0, 0, 0.10)',
              },
            }}
          >
            <Button
              disabled={!hasServiceAvailable}
              m="auto"
              fullWidth
              size="md"
              mih={rem(48)}
              onClick={handleSelectService}
            >
              Xác nhận
            </Button>
          </Card>
        </>
      );
    }

    return servicesContent;
  }, [
    isTruckService,
    truckGroupedServices,
    truckSelectedGroup,
    getServiceCategories,
    activeItem,
    handleSelectVehicle,
    localeSuffix,
    isEstimatingIntercityOrder,
    isEstimatingNewOrder,
    isMobile,
    hasServiceAvailable,
    handleSelectService,
    surgePriceConfig,
  ]);

  const isBulkyNewFlow = useMemo(() => {
    return (
      truckSelectedGroup === TruckGroup.sameday &&
      (selectedService as any)?.estimation_flow === 'BULKY_NEW_FLOW'
    );
  }, [truckSelectedGroup, selectedService]);
  // Return optimized hook values with proper memoization
  return useMemo(
    () => ({
      isDisabled: isLoadingServiceTypes || !pickupPath,
      isLoading: isLoadingServiceTypes || loadingLocation,
      isHiddenTruck: !isTruckService || !serviceTypesData?.length,
      estimatedServices,
      pickupPath,
      onOpenPopupAllService: handleOpenAllService,
      onClosePopupAllService: close,
      renderEstimatedServices,
      truckGroupedServices,
      truckGroup: truckSelectedGroup,
      isBulkyNewFlow,
    }),
    [
      isTruckService,
      isLoadingServiceTypes,
      loadingLocation,
      pickupPath,
      serviceTypesData,
      estimatedServices,
      handleOpenAllService,
      close,
      renderEstimatedServices,
      truckGroupedServices,
      truckSelectedGroup,
      isBulkyNewFlow,
    ]
  );
}
